import { useState, useEffect, useRef, type JSX } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "../components/ui/InputField";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Table from "../components/ui/Table";
import Card from "../components/ui/Card";
import { FaFileCsv, FaFileExcel, FaSearch } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import Toggle from "../components/ui/Toggle";
import Pagination from "../components/ui/Pagination";
import { useScrape } from "../hooks/useScrape";
import { exportToCSV, exportToExcel, downloadRawCSV, API_URL } from "../services/api";
import LeadViewer from "../components/ui/LeadViewer";
import { useToast } from "../hooks/useToast";
import { LuCheck, LuLoader, LuSparkles, LuX } from "react-icons/lu";

export interface ScrapeRow {
    id: string;
    account: string;
    type: "Followers" | "Following" | "Hashtag Search";
    totalCount: number | "-";
    time: string;
    status: "completed" | "canceled" | "in-progress";
    isVerified: boolean;
    scrapeGoal: string;
    requested: number; // how many the user asked for (for the static ETA)
}

interface Column {
    key: string;
    title: string;
    hidden?: boolean;
    render?: (row: ScrapeRow) => JSX.Element | null;
}

export default function ScrapeHistory() {
    const { jobs, loading, fetchJobs } = useScrape();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [viewingJobId, setViewingJobId] = useState<string | null>(null);
    const [cancellingId, setCancellingId] = useState<string | null>(null);



    const cancelJob = async (jobId: string) => {
        if (cancellingId) return;
        setCancellingId(jobId);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/scrapes/${jobId}/cancel`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error('Failed to cancel');
            showToast({ title: 'Success', message: 'Job cancelled successfully', variant: 'success' });
            fetchJobs();
        } catch {
            showToast({ title: 'Error', message: 'Failed to cancel job', variant: 'error' });
        } finally {
            setCancellingId(null);
        }
    };

    // Track previously in-progress job IDs to detect transitions
    const prevInProgressRef = useRef<Set<string>>(new Set());
    // Track newly completed jobs for the banner
    const [completedAlerts, setCompletedAlerts] = useState<{ id: string; target: string; count: number; goalLabel?: string }[]>([]);

    // Smart Polling for in-progress jobs:
    // Phase 1 (eager, 0-30s): poll every 2s to catch instant cache hits.
    // Phase 2 (normal): poll every 5s only while active jobs exist.
    const hasActiveJobs = jobs.some(j => j.status === 'in-progress');
    const eagerEndRef = useRef(Date.now() + 30_000);

    useEffect(() => {
        const tick = () => {
            const isEager = Date.now() < eagerEndRef.current;
            if (isEager || hasActiveJobs) fetchJobs(true);
        };
        const isEager = Date.now() < eagerEndRef.current;
        const interval = setInterval(tick, isEager ? 2000 : 5000);
        return () => clearInterval(interval);
    }, [hasActiveJobs, fetchJobs]);

    // Detect when jobs transition from in-progress to completed
    useEffect(() => {
        const currentInProgress = new Set(
            jobs.filter(j => j.status === 'in-progress').map(j => j.id)
        );
        const prevInProgress = prevInProgressRef.current;

        // Check if any previously in-progress jobs are now completed
        for (const jobId of prevInProgress) {
            const job = jobs.find(j => j.id === jobId);
            if (job && job.status === 'completed') {
                const goal = job.scrapeGoal || 'emails';
                let count = (job as any).resultCount ?? 0;
                if (!count) {
                    if (goal === 'profiles') count = job.resultsProfiles?.length || 0;
                    else if (goal === 'list') count = job.resultsLists?.length || 0;
                    else count = job.resultsEmails?.length || 0;
                }

                const goalLabel = goal === 'profiles' ? 'profiles' : (goal === 'list' ? 'users' : 'emails');

                showToast({
                    title: "✅ Scraping Done!",
                    message: `Scrape for ${job.target.replace(/^@+/, '')} is complete! Collected ${count} ${goalLabel}. You can now view and export your leads.`,
                    variant: "success",
                    duration: 8000,
                });

                setCompletedAlerts(prev => [
                    ...prev, 
                    { id: job.id, target: job.target, count: count, goalLabel: goalLabel }
                ]);

                // Auto-dismiss the banner after 15 seconds
                setTimeout(() => {
                    setCompletedAlerts(prev => prev.filter(a => a.id !== job.id));
                }, 15000);
            }
        }

        prevInProgressRef.current = currentInProgress;
    }, [jobs, showToast]);

    const scrapeData: ScrapeRow[] = jobs.map(job => {
        const goal = job.scrapeGoal || 'emails';
        // Prefer the lightweight `resultCount` from the history endpoint (the list no
        // longer ships full result arrays). Fall back to array lengths for legacy/cached data.
        let count = (job as any).resultCount ?? 0;
        if (!count) {
            if (goal === 'profiles') count = job.resultsProfiles?.length || 0;
            else if (goal === 'list') count = job.resultsLists?.length || 0;
            else count = job.resultsEmails?.length || 0;
        }

        return {
            id: job.id,
            account: job.target,
            type: job.target.includes('#') ? 'Hashtag Search' : (job.scrapeType === 'followers' ? 'Followers' : 'Following'),
            totalCount: count || "-",
            time: formatTime(job.timestamp),
            status: job.status as "completed" | "canceled" | "in-progress",
            isVerified: job.targetIsVerified || false,
            scrapeGoal: goal,
            requested: Number((job as any).requested ?? (job as any).emailLimit ?? 0),
        };
    });

    const isEmpty = !loading && scrapeData.length === 0;

    const toggleRow = (id: string) => {
        setSelectedRows((prev) =>
            prev.includes(id)
                ? prev.filter((i) => i !== id)
                : [...prev, id]
        );
    };

    const handleExport = async (type: 'csv' | 'excel', jobId?: string) => {
        const jobsToExport = jobId 
            ? jobs.filter(j => j.id === jobId)
            : jobs.filter(j => selectedRows.includes(j.id));

        if (jobsToExport.length === 0) {
            alert("No data to export.");
            return;
        }

        try {
            const token = localStorage.getItem('token');
            
            const fullJobsResponses = await Promise.all(
                jobsToExport.map(job => 
                    fetch(`${API_URL}/api/scrapes/${job.id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }).then(async res => {
                        if (!res.ok) throw new Error('Failed to fetch job');
                        const result = await res.json();
                        return result.data;
                    })
                )
            );

            const flatResults = fullJobsResponses.flatMap(job => {
                const goal = job.scrapeGoal || 'emails';
                const source = goal === 'profiles' ? job.resultsProfiles : (goal === 'list' ? job.resultsLists : job.resultsEmails);
                
                return (source || []).map((result: any) => ({
                    target: job.target,
                    scrapeGoal: goal,
                    username: result.username,
                    fullName: result.fullName || '',
                    email: result.email || '',
                    phone: result.contactPhoneNumber || '',
                    biography: result.biography || '',
                    followers: result.followerCount || 0,
                    website: result.externalUrl || '',
                    isVerified: result.isVerified ? 'Yes' : 'No'
                }));
            });
            
            if (flatResults.length === 0) {
               alert("No data to export.");
               return;
            }

            if (type === 'csv') {
                // Efficiency: If downloading a single job and it has pre-generated CSV, use it
                if (jobsToExport.length === 1 && fullJobsResponses[0].csvData) {
                    downloadRawCSV(fullJobsResponses[0].csvData, `scrape_${fullJobsResponses[0].target}_${new Date().toISOString()}.csv`);
                } else {
                    // Fallback to client-side generation for bulk exports
                    exportToCSV(flatResults, `scrape_export_${new Date().toISOString()}.csv`);
                }
            } else {
                exportToExcel(flatResults, `scrape_export_${new Date().toISOString()}.xlsx`);
            }
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export data. Please try again.');
        }
    };

    const dismissAlert = (id: string) => {
        setCompletedAlerts(prev => prev.filter(a => a.id !== id));
    };

    const columns: Column[] = [
        {
            key: "checkbox",
            title: "",
            render: (row) => (
                <input
                    type="checkbox"
                    className="w-4 h-4 cursor-pointer"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                />
            ),
        },
        {
        key: "account",
        title: "Account",
        render: (row) => {
        const cleanAccount = row.account.replace(/^@+/, '');
        const isHashtag = cleanAccount.startsWith('#');
        return (
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-[10px] uppercase shrink-0">
                    {isHashtag ? '#' : cleanAccount.charAt(0)}
                </div>
                <span className="flex items-center gap-1">
                    {(() => {
                        const hasHash = row.account.includes('#');
                        const text = row.account.replace(/@/g, '');
                        return hasHash ? text : `@${text}`;
                    })()}
                    {row.isVerified && (
                        <MdVerified className="text-blue-500" size={16} title="Verified Account" />
                    )}
                </span>
            </div>
        );
        },
        },
        { key: "type", title: "Type" },
        { 
            key: "totalCount", 
            title: "Results Collected",
            render: (row) => (
                <div className="flex flex-col">
                    {/* While running, don't show a live (growing) count — show a static
                        ETA (~1 profile/sec). The real count appears once it completes. */}
                    {row.status === 'in-progress'
                        ? <span className="font-bold text-violet-500">~{row.requested || '—'}s</span>
                        : <span className="font-bold">{row.totalCount}</span>}
                    <span className="text-[10px] text-gray-400 uppercase tracking-widest">
                        {row.status === 'in-progress' ? 'est. time' : row.scrapeGoal}
                    </span>
                </div>
            )
        },
        { key: "time", title: "Time" },
        {
            key: "status",
            title: "Status",
            render: (row) => {
                const getStatusVariant = () => {
                    if (row.status === "completed") return "completed";
                    if (row.status === "in-progress") return "running";
                    return "failed";
                };

                const getStatusText = () => {
                    if (row.status === "completed") return "Completed";
                    if (row.status === "in-progress") return "In Progress";
                    return "Cancelled";
                };

                return (
                    <div className="flex items-center gap-2">
                        {row.status === "in-progress" && (
                            <LuLoader size={14} className="text-violet-600 animate-spin" />
                        )}
                        <Badge variant={getStatusVariant()}>
                            {getStatusText()}
                        </Badge>
                    </div>
                );
            },
        },
        {
            key: "actions",
            title: "Export Emails List",
            hidden: selectedRows.length > 0,
            render: (row) =>
                selectedRows.length === 0 ? (
                    <div className="flex flex-col gap-2">
                        {row.status === 'in-progress' && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="flex items-center gap-2 text-xs text-violet-600 font-semibold px-3 py-2 bg-violet-50 rounded-lg border border-violet-100">
                                    <LuLoader size={12} className="animate-spin" />
                                    Scraping{row.requested ? ` · ~${row.requested}s` : '…'}
                                </div>
                                {/* No live "View Leads" during the run — results open only
                                    once the job finishes (keeps the run fast, no live polling
                                    of growing data). Cancelled/completed still show View Leads. */}
                                <button
                                    onClick={() => cancelJob(row.id)}
                                    disabled={cancellingId === row.id}
                                    className="flex items-center gap-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-2 rounded-lg transition-all disabled:opacity-50"
                                >
                                    {cancellingId === row.id
                                        ? <LuLoader size={12} className="animate-spin" />
                                        : <LuX size={12} />}
                                    Cancel
                                </button>
                            </div>
                        )}
                        {(row.status === 'completed' || (row.status === 'canceled' && typeof row.totalCount === 'number' && row.totalCount > 0)) && (
                            <>
                                <Button 
                                    variant="primary" 
                                    size="sm" 
                                    className="bg-violet-600 hover:bg-violet-700 text-white"
                                    onClick={() => setViewingJobId(row.id)}
                                >
                                    <FaSearch /> View Leads
                                </Button>
                                <Button variant="accent" size="sm" onClick={() => handleExport('csv', row.id)}>
                                    <FaFileCsv /> Export CSV
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleExport('excel', row.id)}>
                                    <FaFileExcel /> Export XLS
                                </Button>
                            </>
                        )}
                    </div>
                ) : null,
        },
    ];

    const filteredData = scrapeData.filter((item) => {
        const query = search.toLowerCase();
        const matchesSearch = 
            item.account.toLowerCase().includes(query) ||
            item.type.toLowerCase().includes(query) ||
            String(item.totalCount).includes(query) ||
            item.status.toLowerCase().includes(query);
        
        const matchesType = typeFilter === 'all' || item.type.toLowerCase() === typeFilter.toLowerCase();
        const matchesVerified = !verifiedOnly || item.isVerified;
        
        return matchesSearch && matchesType && matchesVerified;
    });

    const perPage = 5;
    const totalPages = Math.ceil(filteredData.length / perPage);
    const paginatedData = filteredData.slice((page - 1) * perPage, page * perPage);

    if (loading) {
    return (
        <div className="p-6 space-y-6 bg-purple-50 min-h-screen max-w-screen">
            <div className="flex justify-center items-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
        </div>
    );
}
    return (
        <div className="p-6 space-y-6 bg-purple-50 min-h-screen max-w-screen">
            {/* Completion Alert Banners */}
            {completedAlerts.map(alert => (
                <div 
                    key={alert.id}
                    className="relative overflow-hidden bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-5 text-white shadow-xl shadow-emerald-200/50 animate-in slide-in-from-top-4 duration-500"
                >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 shrink-0">
                                <LuCheck size={24} strokeWidth={3} />
                            </div>
                            <div>
                                <h3 className="text-lg font-black tracking-tight flex items-center gap-2">
                                    <LuSparkles size={18} /> Scraping Done!
                                </h3>
                                <p className="text-emerald-50 text-sm font-medium mt-0.5">
                                    {(() => {
                                        const hasHash = alert.target.includes('#');
                                        const text = alert.target.replace(/@/g, '');
                                        return hasHash ? text : `@${text}`;
                                    })()} — Found <span className="font-black text-white">{alert.count} {alert.goalLabel || 'emails'}</span>. Ready to export!
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <button 
                                onClick={() => {
                                    setViewingJobId(alert.id);
                                    dismissAlert(alert.id);
                                }}
                                className="flex-1 sm:flex-none px-5 py-2.5 bg-white text-emerald-700 rounded-xl font-bold text-sm hover:bg-emerald-50 transition-all shadow-lg active:scale-95"
                            >
                                View Leads
                            </button>
                            <button 
                                onClick={() => {
                                    handleExport('csv', alert.id);
                                    dismissAlert(alert.id);
                                }}
                                className="flex-1 sm:flex-none px-5 py-2.5 bg-emerald-700/40 text-white rounded-xl font-bold text-sm hover:bg-emerald-700/60 transition-all border border-white/20"
                            >
                                Export CSV
                            </button>
                            <button 
                                onClick={() => dismissAlert(alert.id)}
                                className="p-2 text-white/70 hover:text-white transition-colors"
                                title="Dismiss"
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            <div className="flex justify-between items-start flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Scrape History</h1>
                    <p className="text-gray-500 text-sm">Manage your scraping history</p>
                </div>
                {selectedRows.length > 0 && (
                    <div className="flex items-center gap-3">
                        <Button variant="accent" size="md" onClick={() => handleExport('csv')}>
                            <FaFileCsv /> Export CSV
                        </Button>
                        <Button variant="outline" size="md" onClick={() => handleExport('excel')}>
                            <FaFileExcel /> Export XLS
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-4 mt-4">
                <div className="relative w-full sm:w-72">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">
                        <FaSearch />
                    </span>
                    <InputField
                        type="text"
                        placeholder="Search by username, or type"
                        className="pl-10 bg-gray-100 border-gray-200 w-full"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>

                <Toggle
                    variant="default"
                    label="Verified only"
                    checked={verifiedOnly}
                    onChange={setVerifiedOnly}
                    trackColor="bg-gray-400"
                    className="lg:ml-auto p-2 px-3 rounded-md border-2 border-gray-200"
                />

                <InputField
                    type="select"
                    className="bg-gray-100 border-gray-200"
                    value={typeFilter}
                    onChange={(e) => {
                        setTypeFilter(e.target.value);
                        setPage(1);
                    }}
                    options={[
                        { label: "All Type", value: "all" },
                        { label: "Followers", value: "followers" },
                        { label: "Following", value: "following" },
                        { label: "Hashtag Search", value: "hashtag search" },
                    ]}
                />
            </div>

            {isEmpty && (
                <div className="flex flex-col items-center mt-20 text-center">
                    <span className="text-gray-400 text-6xl">📄</span>
                    <p className="mt-4 text-lg font-semibold">No Email List Yet</p>
                    <p className="text-gray-500 mt-1">Get started by New Scrape.</p>
                    <Button 
                        className="mt-5 bg-linear-to-r from-purple-500 to-orange-400 text-white"
                        onClick={() => navigate('/new-scrape')}
                    >
                        Start Scraping
                    </Button>
</div>
            )}

            {!isEmpty && filteredData.length <= 0 && (
                <div className="flex flex-col items-center mt-20 text-center">
                    <span className="text-gray-400 text-6xl">📄</span>
                    <p className="mt-4 text-lg font-semibold">Couldn't find what you asked</p>
                    <p className="text-gray-500 mt-1">Try a New Scrape.</p>
                    <Button 
                        className="mt-5 bg-linear-to-r from-purple-500 to-orange-400 text-white"
                        onClick={() => navigate('/new-scrape')}
                    >
                        Start Scraping
                    </Button>
                </div>
            )}

            {!isEmpty && filteredData.length > 0 && (
                <>
                    <Card className="p-0">
                        <Table columns={columns} data={paginatedData} />
                    </Card>

                    <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </>
            )}

            <LeadViewer 
                jobId={viewingJobId} 
                onClose={() => setViewingJobId(null)} 
            />
        </div>
    );
}

function formatTime(timestamp: string): string {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}